import { useParams, Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site';

function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return views.toString();
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const videoItem = videos.find((v) => v.slug === slug);
  const articleItem = articles.find((a) => a.slug === slug);
  const item = videoItem || articleItem;

  if (!item) {
    const categoryName = categories[0]?.name || '分类';
    return (
      <>
        <SEOMeta title="内容未找到" description="您访问的内容不存在或已被移除" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-20">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '内容未找到' }]} />
            <div className="w-full px-4 md:px-6 py-20">
              <div className="max-w-7xl mx-auto text-center">
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-file-unknow-line text-3xl text-secondary-400"></i>
                </div>
                <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">内容未找到</h1>
                <p className="text-foreground-500 text-sm mb-6">您访问的内容不存在或已被移除</p>
                <Link
                  to="/"
                  className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === item.category);
  const isVideo = 'duration' in item;
  const tags = item.tags || [];

  const relatedItems = isVideo
    ? videos.filter((v) => v.id !== item.id && v.category === item.category).slice(0, 4)
    : articles.filter((a) => a.id !== item.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: item.title,
    description: 'excerpt' in item ? item.excerpt : item.description,
    author: {
      '@type': 'Person',
      name: 'author' in item ? item.author : siteInfo.name,
    },
    datePublished: item.publishDate,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
  };

  const videoSchema = isVideo
    ? {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: item.title,
        description: item.description,
        thumbnailUrl: item.thumbnail,
        uploadDate: item.publishDate,
      }
    : null;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `如何观看《${item.title}》高清版本？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在国产在线一区平台直接观看《${item.title}》的高清版本，支持多种清晰度选择，无需下载即可流畅播放。`,
        },
      },
      {
        '@type': 'Question',
        name: `《${item.title}》支持哪些设备观看？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线一区支持PC、平板、手机等多种设备观看，无需安装任何插件，打开浏览器即可享受高清观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '国产在线一区是否需要注册？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线一区大部分内容支持免注册观看，用户可以自由浏览海量国产高清视频内容。部分独家内容可能需要注册账号后观看。',
        },
      },
    ],
  };

  const schemas = [articleSchema, faqSchema];
  if (videoSchema) schemas.push(videoSchema);

  const pageTitle = `${item.title} - 国产在线一区`;
  const pageDescription = 'excerpt' in item ? item.excerpt : item.description;
  const pageKeywords = `${item.title},国产在线一区,国产视频在线观看,${tags.join(',')}`;

  return (
    <>
      <SEOMeta
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl={`${siteInfo.url}/article/${item.slug}`}
        ogType="article"
        schema={schemas}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(categoryInfo ? [{ label: categoryInfo.name, href: `/category/${categoryInfo.slug}` }] : []),
              { label: item.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 py-6 md:py-10">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                  {item.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  {'author' in item && (
                    <span className="whitespace-nowrap">
                      <strong className="text-foreground-700">{item.author}</strong>
                    </span>
                  )}
                  <span className="whitespace-nowrap">{item.publishDate}</span>
                  {'readTime' in item && (
                    <span className="whitespace-nowrap">{item.readTime}阅读</span>
                  )}
                  <span className="whitespace-nowrap">{formatViews(item.views)} 次浏览</span>
                </div>
              </header>

              {/* Thumbnail */}
              <div className="w-full aspect-video rounded-lg overflow-hidden mb-10">
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  title={`${item.title} - 国产在线一区高清视频`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Video Info Bar */}
              {isVideo && (
                <div className="flex items-center gap-4 p-4 bg-background-100 rounded-lg mb-8 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-timer-line text-foreground-500"></i>
                    </div>
                    <span className="text-sm text-foreground-700 whitespace-nowrap">{item.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-4k-line text-foreground-500"></i>
                    </div>
                    <span className="text-sm text-foreground-700 whitespace-nowrap">{item.quality}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-eye-line text-foreground-500"></i>
                    </div>
                    <span className="text-sm text-foreground-700 whitespace-nowrap">{formatViews(item.views)} 次播放</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <span className="text-sm text-accent-600 font-semibold whitespace-nowrap">{item.rating} 分</span>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="prose-custom">
                {'content' in item ? (
                  <div
                    className="text-foreground-800 leading-relaxed text-sm md:text-base space-y-4"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                ) : (
                  <div className="text-foreground-800 leading-relaxed text-sm md:text-base">
                    <p className="mb-4">{item.description}</p>
                    <p className="mb-4">
                      国产在线一区为您提供《{item.title}》的高清完整版本，画质清晰，播放流畅，让您足不出户就能享受影院级的观影体验。本内容支持多种清晰度选择，可根据网络环境自由切换，确保最佳观看效果。
                    </p>
                    <p className="mb-4">
                      我们致力于为用户提供最优质的国产视频内容，所有视频均经过精心筛选和优化处理，画质清晰、色彩鲜艳、音效出色，为您带来沉浸式的观看体验。
                    </p>
                    <p>
                      如果您喜欢《{item.title}》，欢迎浏览我们平台上的更多精彩国产视频内容。国产在线一区持续更新海量高清视频，涵盖电影、电视剧、综艺、纪录片、动漫等多个品类，总有您喜欢的精彩内容。
                    </p>
                  </div>
                )}
              </div>

              {/* Tags Footer */}
              <div className="mt-10 pt-6 border-t border-background-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
                  {tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {/* Related Content */}
          <section className="w-full px-4 md:px-6 py-10 md:py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className={`grid grid-cols-1 sm:grid-cols-2 ${relatedItems.length >= 4 ? 'lg:grid-cols-4' : 'md:grid-cols-3'} gap-5`} data-product-shop="">
                {relatedItems.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="w-full aspect-video overflow-hidden">
                      <img
                        src={related.thumbnail}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1 leading-snug">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-400 whitespace-nowrap">{related.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
}